// Professional Login.tsx with background videos
import React, { useState, useEffect, useRef } from 'react';
import { useHistory, Link } from 'react-router-dom';
import '../../../styles/components/Login.scss';
import logo from '../../../assets/vivvy-logo.png';
import { supabase } from '../../../helpers/Supabase';

const Login: React.FC = () => {
  const history = useHistory();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(1);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Background video rotation
  useEffect(() => {
    const videoInterval = setInterval(() => {
      setCurrentVideo(prev => prev === 4 ? 1 : prev + 1);
    }, 8000); // Change video every 8 seconds

    return () => clearInterval(videoInterval);
  }, []);

  // Video transition effect
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.style.opacity = '0';
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.style.opacity = '1';
        }
      }, 500);
    }
  }, [currentVideo]);

  // Floating particles effect
  useEffect(() => {
    const createParticle = () => {
      const particle = document.createElement('div');
      particle.className = 'particle';
      particle.style.left = Math.random() * 100 + '%';
      particle.style.top = Math.random() * 100 + '%';
      particle.style.width = Math.random() * 3 + 1 + 'px';
      particle.style.height = particle.style.width;
      particle.style.animationDelay = Math.random() * 6 + 's';
      particle.style.animationDuration = (Math.random() * 4 + 4) + 's';
      
      const container = document.querySelector('.login-container');
      if (container) {
        container.appendChild(particle);
        
        // Remove particle after animation
        setTimeout(() => {
          if (particle.parentNode) {
            particle.parentNode.removeChild(particle);
          }
        }, 12000);
      }
    };

    // Create particles periodically
    const particleInterval = setInterval(createParticle, 3000);
    
    // Create initial particles
    for (let i = 0; i < 8; i++) {
      setTimeout(createParticle, i * 300);
    }

    return () => clearInterval(particleInterval);
  }, []);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
  
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: form.email,
        password: form.password,
      });
    
      if (error) {
        // Check if this might be a delegate who hasn't set up their password
        if (error.message.includes('Invalid login credentials')) {
          // Check if this email is associated with a delegate invitation
          const { data: clientDelegate } = await supabase
            .from('client_delegates')
            .select('*')
            .eq('email', form.email)
            .eq('is_active', true)
            .single();

          const { data: storeDelegate } = await supabase
            .from('store_delegates')
            .select('*')
            .eq('email', form.email)
            .eq('is_active', true)
            .single();

          if (clientDelegate || storeDelegate) {
            setError('This email is associated with a delegate invitation. Please check your email for the invitation link to set up your account.');
          } else {
            setError(error.message);
          }
        } else {
          setError(error.message);
        }
      } else {
        // 👇 force refresh session to ensure user ID + token is valid
        await supabase.auth.refreshSession();
        
        // Check if user is a delegate
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          // Check for delegate access
          const { data: clientDelegate } = await supabase
            .from('client_delegates')
            .select('*')
            .eq('email', user.email)
            .eq('is_active', true)
            .single();

          const { data: storeDelegate } = await supabase
            .from('store_delegates')
            .select('*')
            .eq('email', user.email)
            .eq('is_active', true)
            .single();

          if (clientDelegate || storeDelegate) {
            // User is a delegate, redirect to dashboard
            history.push('/dashboard');
          } else {
            // Check if user is a regular client user
            const { data: userRow } = await supabase
              .from('users')
              .select('client_id')
              .eq('auth_user_id', user.id)
              .single();

            if (userRow?.client_id) {
              // Regular client user
              history.push('/dashboard');
            } else {
              // User exists but has no access - could be a delegate who hasn't accepted invitation
              setError('No access found. Please check your invitation email or contact your administrator.');
            }
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Background Videos */}
      <div className="background-videos">
        <video
          ref={videoRef}
          className={`background-video ${currentVideo === 1 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../../assets/bg-1.mp4')}
        />
        <video
          className={`background-video ${currentVideo === 2 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../../assets/bg-2.mp4')}
        />
        <video
          className={`background-video ${currentVideo === 3 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../../assets/bg-3.mp4')}
        />
        <video
          className={`background-video ${currentVideo === 4 ? 'active' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          src={require('../../../assets/bg-4.mp4')}
        />
        <div className="video-overlay"></div>
      </div>

      {/* Floating particles container */}
      <div className="floating-particles"></div>
      
      {/* Professional Content */}
      <div className="login-content">
        <div className="brand-section">
          <img src={logo} alt="Vivvy Logo" className="brand-logo" />
          <div className="brand-text">
            <h1>Welcome Back</h1>
            <p>Continue your journey with intelligent customer engagement</p>
          </div>
        </div>

        <div className="form-section">
          <div className="form-container">
            <div className="form-header">
              <h2>Sign In</h2>
              <p>Access your Vivvy dashboard</p>
            </div>

            <form className="login-form" onSubmit={handleLogin}>
              <div className="input-group">
                <label htmlFor="email">Email Address</label>
                <input 
                  id="email"
                  type="email" 
                  name="email" 
                  value={form.email}
                  onChange={handleInput} 
                  placeholder="Enter your email address"
                  required 
                />
              </div>
              <div className="input-group">
                <label htmlFor="password">Password</label>
                <input 
                  id="password"
                  type="password" 
                  name="password" 
                  value={form.password}
                  onChange={handleInput} 
                  placeholder="Enter your password"
                  required 
                />
              </div>
              <button type="submit" className="submit-button" disabled={loading}>
                {loading ? (
                  <>
                    <div className="loading-spinner"></div>
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
              {error && <div className="error-message">{error}</div>}
            </form>

            <div className="form-footer">
              <Link to="/forgot-password" className="forgot-link">Forgot Password?</Link>
              <p>
                Don't have an account? <Link to="/onboarding" className="signup-link">Sign Up</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
